<script setup lang="ts">
    import { computed, shallowReactive, onMounted, watch, ref, type Ref } from 'vue'
    import { useRoute } from 'vue-router'
    import { generateName } from '@common/Common'
    import { guid } from '@common/Guid'
    import PortfolioNavigation from '@common/Components/PortfolioNavigation.vue'
    import Navigation from '@common/Navigation.vue'
    import ProjectNavigation from '@Quest/ProjectPlans/Components/ProjectNavigation.vue'
    import ProjectContextHeader from '@Quest/ProjectPlans/Components/ProjectContextHeader.vue'
    import { useProjectPlanSettingsPageQueryV1 } from '@Quest/ProjectPlans/queries-vue-composables'
    import { useUpdateProjectPlanCommandV1 } from '@Quest/ProjectPlans/commands-vue-composables'
    import type { ProjectPlanSettingsPageReadModel } from '@Quest/ProjectPlans/readmodels'
    import { CurrencyDenomination } from '../../../_Common/types'

    const route = useRoute()
    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const tchProjectId = computed(() => Number(route.params.tchProjectId ?? 0))
    const projectId = computed(() => String(route.params.projectId ?? ''))
    
    const rm = ref<ProjectPlanSettingsPageReadModel | null>(null);
    const projectPlanId = computed<string | null>(() => rm.value?.ProjectPlanId ?? null)
    const projectName = computed(() => rm.value?.ProjectName ?? '')
    const projectPlanName = computed<string>(() => rm.value?.ProjectPlanName ?? '')
    const activePlanId = computed<string | null>(() => rm.value?.ActiveProjectPlanId ?? null)

    const portfolioId = computed(() => rm.value?.PortfolioId ?? '')
    const userId = computed(() => rm.value?.UserId)
    const isWorkspaceMember = computed(() => rm.value?.IsWorkspaceMember ?? false)
    const isProjectOwner = computed(() => rm.value?.IsProjectOwner ?? false)

    const name = ref<string>('')
    const granularity = ref<string | null>(null)
    const denomination = ref<CurrencyDenomination>(CurrencyDenomination.Million)
    const durationInMonths = ref<number>(72)
    const submissionDueDate = ref<string | null>(null)

    const {
        runProjectPlanSettingsPageQueryV1,
        modelProjectPlanSettingsPageQueryV1
    } = useProjectPlanSettingsPageQueryV1(workspaceId, tchProjectId, projectId, projectPlanId)

    const {
        invokeUpdateProjectPlanCommandV1,
        onSucceedUpdateProjectPlanCommandV1
    } = useUpdateProjectPlanCommandV1(userId, workspaceId, portfolioId, projectId, projectPlanId, name, durationInMonths, granularity, denomination, submissionDueDate)

    onMounted(() =>
    {
        document.title = 'Project | TransparentChoice'

        runProjectPlanSettingsPageQueryV1({
            ProjectPlanId: route.params.planId ? String(route.params.planId) : null,
        })
    })

    watch(() => route.params.planId, () => 
    {
        runProjectPlanSettingsPageQueryV1({
            ProjectPlanId: route.params.planId as string | null
        })
    })

    watch(modelProjectPlanSettingsPageQueryV1, (current) =>
    {
        if (current?.RedirectUrl)
        {
            window.location.replace(current.RedirectUrl)
        }

        if (current)
        {
            rm.value = current
            name.value = rm.value.ProjectPlanName
            granularity.value = rm.value.Granularity
            denomination.value = rm.value.Denomination
            durationInMonths.value = rm.value.DurationInMonths
            submissionDueDate.value = rm.value.SubmissionDueDate
        }
    })

    onSucceedUpdateProjectPlanCommandV1(() =>
    {
        alert('done')
        runProjectPlanSettingsPageQueryV1()
    })

</script>

<template>
    <div>
        <PortfolioNavigation v-if="isWorkspaceMember" :workspaceId="workspaceId" :projectId="tchProjectId" />
        <Navigation v-else-if="isProjectOwner" />

        <div v-if="rm" class="container-fluid" style="margin-top: 80px; padding-top: 40px;">

            <div class="row">
                <div class="col-lg-6">
                    <ProjectContextHeader :projectName="projectName"
                                          :planId="projectPlanId"
                                          :planName="projectPlanName"
                                          :activePlanId="activePlanId" />
                </div>
                <div class="col-lg-6 text-right align-self-end">
                </div>
            </div>
            <hr />

            <div v-if="rm">

                <ProjectNavigation ActivePage="settings" class="mt-4 mb-4"
                                   :workspaceId="workspaceId"
                                   :tchProjectId="tchProjectId"
                                   :projectId="projectId"
                                   :planId="projectPlanId" />

                <div class="tc-settings-container">

                    <form id="project-plan-editor-form" @submit.prevent="invokeUpdateProjectPlanCommandV1">

                        <!-- ===== Plan settings ===== -->
                        <div class="card tc-card mb-4">
                            <div class="card-body">

                                <h5 class="tc-section-title">Settings</h5>

                                <div class="form-row">
                                    <div class="form-group col-md-12">
                                        <label>Name</label>
                                        <input type="text"
                                               v-model.trim="name"
                                               class="form-control"
                                               required minlength="2" maxlength="100" />
                                    </div>

                                    <div class="form-group col-md-6">
                                        <label>Granularity</label>
                                        <select v-model="granularity" class="form-control">
                                            <option value="Monthly">Monthly</option>
                                            <option value="Quarterly">Quarterly</option>
                                            <option value="Yearly">Yearly</option>
                                        </select>
                                    </div>

                                    <div class="form-group col-md-6">
                                        <label>Currency denomination</label>
                                        <select v-model="denomination" class="form-control">
                                            <option value="Unit">Unit</option>
                                            <option value="Thousand">Thousand</option>
                                            <option value="Million">Million</option>
                                        </select>
                                    </div>

                                    <div class="form-group col-md-6">
                                        <label>Duration (in months)</label>
                                        <input type="number"
                                               v-model.number="durationInMonths"
                                               class="form-control"
                                               required min="1" step="1" />
                                    </div>

                                    <div class="form-group col-md-6">
                                        <label>Submission due date</label>
                                        <input type="date"
                                               class="form-control"
                                               v-model="submissionDueDate" />
                                    </div>



                                </div>

                            </div>
                        </div>

                    </form>

                    <!-- ===== Actions ===== -->
                    <div class="tc-actions">
                        <button type="submit"
                                form="project-plan-editor-form"
                                class="btn btn-primary tc-save-btn">
                            Save changes
                        </button>
                    </div>

                </div>

            </div>
        </div>
    </div>
</template>
<style scoped>
    /* ===== Container ===== */

    .tc-settings-container {
        max-width: 1100px;
    }


    /* ===== Cards ===== */

    .tc-card {
        border: 1px solid #e6e9ef;
        border-radius: 10px;
        background: #ffffff;
        box-shadow: 0 2px 6px rgba(0,0,0,0.04);
    }


    /* ===== Section titles ===== */

    .tc-section-title {
        font-size: 1.2rem;
        font-weight: 600;
        color: #2f3a4a;
        margin-bottom: 18px;
    }


    /* ===== Labels ===== */

    .form-group label {
        font-size: 0.85rem;
        margin-bottom: 4px;
        color: #5f6c7b;
    }

    .tc-label-strong {
        font-weight: 500;
        color: #33475b;
    }


    /* ===== Duration ===== */

    .tc-duration-box {
        background: #f8fafc;
        border: 1px solid #e6e9ef;
        border-radius: 8px;
        padding: 12px 14px;
        display: inline-block;
    }

    .tc-duration-value {
        display: flex;
        align-items: baseline;
        gap: 6px;
    }

    .tc-duration-input {
        width: 80px;
        font-size: 1.2rem;
        font-weight: 600;
        border: none;
        background: transparent;
        outline: none;
    }

    .tc-duration-unit {
        font-size: 0.9rem;
        color: #6b7a8c;
    }

    .tc-duration-meta {
        font-size: 0.75rem;
        color: #8a97a6;
    }


    /* ===== Phases ===== */

    .tc-phases-container {
        margin-top: 20px;
        padding-top: 16px;
        border-top: 1px dashed #e6e9ef;
    }

    .tc-phases-header {
        display: flex;
        flex-direction: column;
        margin-bottom: 10px;
        font-weight: 500;
    }

    .tc-phases-sub {
        font-size: 0.8rem;
        color: #8a97a6;
    }


    /* ===== Actions ===== */

    .tc-actions {
        position: sticky;
        bottom: 20px;
        display: flex;
        justify-content: flex-end;
        margin-top: 20px;
    }

    .tc-save-btn {
        min-width: 140px;
    }
</style>
